import axios from 'axios';
import * as moment from 'moment';
import * as React from 'react';
import { useRecoilValue } from 'recoil';
import { AEventTypes } from '../../../atoms/global/events';
import { AUsers } from '../../../atoms/global/users';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import DropdownTagsCloud from '../../../components_v2/dropdown/DropdownTagsCloud';
import Switch from '../../../components_v2/Switch/Switch';
import { Column, Table } from '../../../components_v2/table/Table';
import { URL_FOUNDATION } from '../../../config/keys';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { Translate, translateToString } from '../../../styles/global/translate';
import { launchToastError, launchToastMessage } from '../../forms/visual-form-editor/visualFormEditor';
import { ToolbarState } from '../../globals/mainPage/mainPage';
import { TableRow } from '../../orders/templateOrders/style/Style';
import Restricted from '../../permissions/Restricted';
import { FlexDiv } from '../../products/style';
import { getTeams, Team } from '../../teams/actions';
import { DefaultInputBlock } from '../../../styles/global/css/GlobalInput';

// The event frequency rules: per event type, block the creation of events
// violating the client company's visit frequency (365/frequency days between
// two events of the same type). The whole set saves at once — the API's PUT
// replaces the company's rules.

type EventFrequencyRule = {
	event_type_id: number,
	mode: 'off' | 'block',
	resolution: 'declassify',
	// the constrained scope is the union of both, resolved server-side at
	// check time so a rule follows its teams; both null constrains everyone
	user_scope: number[] | null,
	team_scope: number[] | null,
	// lateness base for companies without any event of this type: elapsed
	// since this date instead of the company's creation date; a row may
	// carry only this (mode 'off')
	base_date: string | null,
};

type RuleDraft = { block: boolean, userScope: number[], teamScope: number[], baseDate: string };
type BaseMode = 'creation' | 'custom';

function getRules(): Promise<EventFrequencyRule[]> {
	return axios.get(`${URL_FOUNDATION}/api/v2/event-frequency-rules`).then(res => res.data);
}

function putRules(rules: EventFrequencyRule[]): Promise<void> {
	return axios.put(`${URL_FOUNDATION}/api/v2/event-frequency-rules`, rules).then(() => undefined);
}

export default function EventFrequencyRules(props: {
	setToolBarState: (value: ToolbarState) => void
}): JSX.Element {
	const eventTypes = useRecoilValue(AEventTypes);
	const users = useRecoilValue(AUsers);
	const activeUsers = React.useMemo(() => users.filter(user => user.status).map(user => ({ id: user.id, name: user.name })), [users]);
	const [drafts, setDrafts] = React.useState<Map<number, RuleDraft>>(new Map());
	const [teams, setTeams] = React.useState<Team[]>([]);

	React.useEffect(() => {
		getRules().then(rules => setDrafts(new Map(rules.map(rule => [rule.event_type_id, {
			block: rule.mode === 'block',
			userScope: rule.user_scope ?? [],
			teamScope: rule.team_scope ?? [],
			baseDate: rule.base_date ?? '',
		}])))).catch(console.error);
		getTeams().then(setTeams).catch(console.error);
	}, []);

	const setDraft = (eventTypeId: number, patch: Partial<RuleDraft>) => setDrafts(prev => {
		const next = new Map(prev);
		const current = next.get(eventTypeId) ?? { block: false, userScope: [], teamScope: [], baseDate: '' };
		next.set(eventTypeId, { ...current, ...patch });
		return next;
	});

	const save = React.useCallback((current: Map<number, RuleDraft>) => {
		// a row survives when it blocks or carries a base date: the base can
		// exist without blocking anything
		const rules: EventFrequencyRule[] = [...current.entries()]
			.filter(([, draft]) => draft.block || draft.baseDate)
			.map(([event_type_id, draft]) => ({
				event_type_id,
				mode: draft.block ? 'block' : 'off',
				resolution: 'declassify',
				user_scope: draft.block && draft.userScope.length > 0 ? draft.userScope : null,
				team_scope: draft.block && draft.teamScope.length > 0 ? draft.teamScope : null,
				base_date: draft.baseDate || null,
			}));
		putRules(rules)
			.then(() => launchToastMessage(translateToString('event_frequency.saved')))
			.catch(e => {
				console.error(e);
				launchToastError(translateToString('error_please_retry'));
			});
	}, []);

	React.useEffect(() => {
		props.setToolBarState({
			bottomRightToolbarComponent: <Restricted to={{ objectAction: 'UpdateCompanySettings' }}>
				<DefaultButton margin='0.5em' onClick={() => save(drafts)}><Translate id='save' /></DefaultButton>
			</Restricted>,
		});
	}, [drafts]);

	const baseModeOptions = React.useMemo(() => [
		{ label: translateToString('event_frequency.base_creation'), value: 'creation' as BaseMode },
		{ label: translateToString('event_frequency.base_custom'), value: 'custom' as BaseMode },
	], []);

	const columns = React.useMemo<Column<{ id: number, name: string }>[]>(() => [
		{
			id: 'name',
			Header: translateToString('type'),
			accessor: row => <TableRow fontWeight='500'>{translateToString(`event.${row.name}`)}</TableRow>,
			width: undefined,
		},
		{
			id: 'block',
			Header: translateToString('event_frequency.block'),
			accessor: row => <Switch
				value={drafts.get(row.id)?.block ?? false}
				onChange={value => setDraft(row.id, { block: value })}
			/>,
			width: 160,
			disableSortBy: true,
		},
		{
			id: 'team_scope',
			Header: translateToString('event_frequency.team_scope'),
			accessor: row => {
				const draft = drafts.get(row.id);
				if (!draft?.block) return <TableRow>-</TableRow>;
				const selected = teams.filter(team => draft.teamScope.includes(team.id));
				return <DropdownTagsCloud
					addImageSize='1.25em'
					NoneContainer={<div>-</div>}
					horizontal
					addImageOnHover
					dropdownSearch
					selected={selected}
					onChange={values => setDraft(row.id, { teamScope: values.map(value => value.id) })}
					onDelete={id => setDraft(row.id, { teamScope: draft.teamScope.filter(teamId => teamId !== id) })}
					tags={teams}
				/>;
			},
			width: undefined,
			disableSortBy: true,
		},
		{
			id: 'scope',
			Header: translateToString('event_frequency.scope'),
			accessor: row => {
				const draft = drafts.get(row.id);
				if (!draft?.block) return <TableRow>-</TableRow>;
				const selected = activeUsers.filter(user => draft.userScope.includes(user.id));
				return <DropdownTagsCloud
					addImageSize='1.25em'
					NoneContainer={<div>{draft.teamScope.length > 0 ? '-' : translateToString('all_users')}</div>}
					horizontal
					addImageOnHover
					dropdownSearch
					selected={selected}
					onChange={values => setDraft(row.id, { userScope: values.map(value => value.id) })}
					onDelete={id => setDraft(row.id, { userScope: draft.userScope.filter(userId => userId !== id) })}
					tags={activeUsers}
				/>;
			},
			width: undefined,
			disableSortBy: true,
		},
		{
			id: 'base_date',
			Header: translateToString('event_frequency.base_label'),
			accessor: row => {
				const draft = drafts.get(row.id);
				const mode: BaseMode = draft?.baseDate ? 'custom' : 'creation';
				return <FlexDiv gap='8px'>
					<Dropdown<BaseMode>
						name={`frequency_base_mode_${row.id}`}
						dropdownStyle={{ width: '210px', optionWidth: '210px' }}
						datalist={baseModeOptions}
						selectedValue={baseModeOptions.find(option => option.value === mode)}
						onChange={value => {
							if (value.value === 'creation') setDraft(row.id, { baseDate: '' });
							else setDraft(row.id, { baseDate: draft?.baseDate || moment().format('YYYY-MM-DD') });
						}}
					/>
					{mode === 'custom' && <DefaultInputBlock
						type='date'
						value={draft?.baseDate ?? ''}
						onChange={e => setDraft(row.id, { baseDate: e.target.value })}
					/>}
				</FlexDiv>;
			},
			width: 380,
			disableSortBy: true,
		},
	], [drafts, activeUsers, teams]);

	return <div style={{ padding: '1em 1em 0', height: '100%', overflowY: 'auto' }}>
		<Table
			noBorder
			height='calc(100vh - 220px)'
			columns={columns}
			data={eventTypes}
		/>
	</div>;
}
